/**
 * Where the writer for one stage requirement actually lives — TRBUD-156.
 *
 * Eden, 3 September 2026, after using the work-order screen:
 *
 * > I want to be able to assign or upload the required info from this
 * > requirements list here rather than having to search the sub menus OR at least
 * > highlighting where the data entry points are in the various sections of the
 * > WOs page.
 *
 * > final invoice and fault time fields — these are required but I cannot find
 * > where to enter them.
 *
 * ## What this module is, and what it deliberately is NOT
 *
 * It is a **map from a requirement key to the control that already satisfies
 * it**. It answers "where do I type this", nothing else. It does not decide
 * whether a requirement is satisfied — `transition_work_order` decides that and
 * `evaluateRequiredFields` (one evaluator, `apps/api/src/read/stages.ts`) is the
 * only thing the API reads it through. A second opinion about satisfaction is the
 * defect migration 0037 exists to end, and a routing table is not one.
 *
 * So every value here is either a place on the work-order screen, a route with
 * this work order pre-selected, or an honest sentence saying no control exists.
 * **A missing writer is reported as missing, never as a disabled button with a
 * hopeful tooltip** — CLAUDE.md's cold-start rule in the direction that matters
 * from the operator's chair: a required field nobody can locate and a required
 * field nobody can write look identical from the chair, and only one of them can
 * be fixed by looking harder.
 *
 * ## Three keys had a granted column and no route — all three now have one
 *
 * `tb_app` holds `UPDATE (primary_vendor_id)`, `UPDATE (meter_reading_id)` and
 * `UPDATE (custom_fields)` on `work_order` — checked against the live grant
 * surface, not inferred — and for a long time **no route used any of the three.**
 * They were set by `create_work_order` and by the intake conversion and never
 * again, which is a required field with no writer: not a strict product, a broken
 * one.
 *
 * The first two got writers on 8 September 2026 (TRBUD-187 remedy (a)):
 * `PATCH /work-orders/:id/vendor` and `PATCH /work-orders/:id/meter-reading`. The
 * third got one on 14 September 2026 (TRBUD-249):
 * `PATCH /work-orders/:id/custom-fields`, whose card is the `custom-fields`
 * anchor below. **The grant needed no change for any of them** — all three
 * columns were already in `tb_app`'s column list in `02-grants.sql`, and
 * `scripts/verify-schema.mjs` already asserted `work_order.custom_fields` ALLOWED
 * ("validated by a trigger, not by a grant"). What was missing was never the
 * privilege; it was the route.
 *
 * ## A writer is not the same thing as a remedy — TRBUD-247
 *
 * `builtin:location_at_open` has a writer and still cannot be required, because
 * the writer runs once inside `create_work_order` and never again. See
 * `manualRemedy` on `RequirementEntry` and `requirementWriterStatus` at the foot
 * of this file.
 */
import type { Capabilities, RequiredFieldState } from "./wire.js";
import { isBuiltinRequiredField } from "./vocabulary.js";

/**
 * One row of the requirements panel: the gate's verdict, why, and where to go.
 *
 * `satisfied`, `label`, `kind` and `dataType` are `RequiredFieldState` verbatim —
 * the same object `evaluateRequiredFields` produces and the transition route's
 * 422 carries. Extending it rather than restating it is what keeps the panel and
 * the refusal message from drifting: there is one evaluator, and this is a
 * decoration on its output.
 */
export interface WorkOrderRequirement extends RequiredFieldState {
  /**
   * WHICH RECORD satisfies it, in the reader's language — "Invoice 88213 from
   * Rush Truck Centers", not "true".
   *
   * Read alongside the verdict rather than derived from it, so it is prose and
   * never evidence. Null when the requirement is unsatisfied, and null is also
   * legal when it IS satisfied and nothing nameable came back — the panel then
   * says satisfied with no explanation rather than inventing one. That
   * degradation is deliberate: it means a disagreement between the verdict and
   * the description can only ever lose a sentence, never flip a verdict.
   */
  basis: string | null;
  entry: RequirementEntry;
}

/** A stage, named the way the panel needs it. */
export interface RequirementStageRef {
  id: string;
  code: string;
  label: string;
}

export interface WorkOrderRequirementsResponse {
  workOrderId: string;
  /**
   * The stage being LEFT, because this is an EXIT gate.
   *
   * `transition_work_order` evaluates `v_source.required_fields` — migration
   * `0056` settled that a stage's requirements gate the move OUT of it, not the
   * move in — so the requirements belong to where the work order is standing.
   */
  stage: RequirementStageRef;
  /**
   * Forward moves this gate applies to. Empty means a terminal stage, where
   * nothing is gated because nothing leaves.
   */
  gatedTransitions: RequirementStageRef[];
  /**
   * Backward moves, which are NEVER gated: the function checks
   * `v_target.sequence > v_source.sequence` before it looks at a single required
   * field. Carried so the panel can say so rather than implying a missing field
   * blocks every move.
   */
  ungatedTransitions: RequirementStageRef[];
  requirements: WorkOrderRequirement[];
  missingCount: number;
}

/**
 * How to reach the writer.
 *
 * - `panel` — a card on the work-order screen. Open it and scroll to it.
 * - `header` — the header row of the work-order screen, where the inline ✎
 *   controls live.
 * - `route` — a different screen, with this work order pre-selected in the URL.
 * - `prerequisite` — the writer exists but needs something else first (an
 *   invoice cannot be itemised before the file is attached).
 * - `automatic` — nobody types it. A collector or the create path fills it.
 * - `unreachable` — the column is granted and no route writes it. See the header.
 * - `misconfigured` — the stage names a key that is not a published field, so
 *   nothing on any work order can satisfy it. README section 9a(d).
 */
export const requirementEntryKinds = [
  "panel",
  "header",
  "route",
  "prerequisite",
  "automatic",
  "unreachable",
  "misconfigured",
] as const;
export type RequirementEntryKind = (typeof requirementEntryKinds)[number];

/**
 * The anchors the work-order screen publishes.
 *
 * A closed list rather than free text, so a rename here is a type error at every
 * card instead of a link that silently scrolls nowhere. The screen sets the same
 * strings as element ids.
 */
export const requirementAnchors = [
  "wo-header",
  "wo-time",
  "wo-costs",
  "wo-documents",
  "wo-procedure",
  // TRBUD-249. The card that writes `work_order.custom_fields`; see the custom
  // arm of `requirementEntryFor` for why this stopped being `unreachable`.
  "custom-fields",
] as const;
export type RequirementAnchor = (typeof requirementAnchors)[number];

export interface RequirementEntry {
  kind: RequirementEntryKind;
  /** The card to open and scroll to. Null for every kind but `panel` / `header`. */
  anchor: RequirementAnchor | null;
  /** A path with this work order pre-selected. Null unless `kind` is `route`. */
  href: string | null;
  /**
   * The capability the write needs — the one the ROUTE requires, not the one the
   * button happens to be gated on today. Where those two disagreed the button
   * was wrong: `TimeCard`'s "Log time" was gated on `postCosts` while
   * `POST /work-orders/:id/time` requires `postTime`, so the mechanic who did the
   * work could not log the hours that gate his own job.
   *
   * Null when there is nothing to press, in which case `note` says why.
   */
  capability: keyof Capabilities | null;
  /** Button text. Null when `kind` admits no action. */
  action: string | null;
  /** Why there is nothing to press. Always set when `action` is null. */
  note: string | null;
  /**
   * What to tell somebody whose role may not do it — the specific role that can,
   * not "you lack a permission". Null when `capability` is null.
   */
  deniedNote: string | null;
  /**
   * `false` on an `automatic` entry whose writer runs ONCE and can never be run
   * again — TRBUD-247.
   *
   * Only ever set on `automatic`, and only to `false`. Absent means the ordinary
   * case: either somebody types the value, or the thing that fills it in can fill
   * it in again later (a collector poll, a projection re-run).
   *
   * It exists because `automatic` alone conflates two situations that look
   * identical on the panel and are opposites at the Configuration screen. A
   * collector-fed key is blank *today*; the next poll fills it, so a stage may
   * gate on it and the work order clears the gate when the data arrives. But
   * `builtin:location_at_open` is captured in the `create_work_order` statement
   * and nowhere else: a work order raised for a unit with no GPS fix has a blank
   * column for ever, and no operator action and no later poll can change that. A
   * stage requiring it would strand exactly those work orders with nothing to
   * press — the cold-start softlock CLAUDE.md describes, arriving through a
   * configuration choice rather than through code.
   *
   * So this is the fact `requirementWriterStatus` reads to tell "no writer at all"
   * from "a writer that has already been and gone", and the guard refuses the two
   * with different words because the remedies differ: one is "build the writer",
   * the other is "gate on something else".
   */
  manualRemedy?: false;
  /**
   * The plain sentence of what to do, shown beside a MISSING requirement even
   * when there is a control to press — TRBUD-300.
   *
   * Only set where one requirement can be met more than one way, because that is
   * the case a single button cannot describe: `builtin:parts_accounted_for` is an
   * issued part OR a part cost OR a verified receipt OR "No parts used", and they
   * live on two different cards. `note` is not reused for this because the panel prints a
   * note only when there is NOTHING to press, and widening that rule would start
   * printing every other entry's explanatory note as body text.
   *
   * Absent means the label and the action already say it.
   */
  instruction?: string;
}

export interface RequirementEntryContext {
  workOrderId: string;
  /**
   * The newest attached invoice- or receipt-class document that could still
   * satisfy a paperwork gate: not detached, and not condemned by a scan verdict.
   *
   * Null is what turns the two money requirements from `route` into
   * `prerequisite` — `/review/new` needs a `documentId` and refuses to render
   * without one, so linking there before anything is attached would be a button
   * that lands on an empty form.
   */
  loggableDocument: { documentId: string; attachmentId: string } | null;
}

const AUTOMATIC: RequirementEntry = {
  kind: "automatic",
  anchor: null,
  href: null,
  capability: null,
  action: null,
  note: null,
  deniedNote: null,
};

/** Where one requirement key is entered. Pure — no data, no verdict, no I/O. */
export function requirementEntryFor(
  key: string,
  dataType: string | null,
  ctx: RequirementEntryContext,
): RequirementEntry {
  if (!isBuiltinRequiredField(key)) {
    /*
     * A `builtin:` key that is not in the registry is a TYPO IN /config, and it
     * has to be checked BEFORE the custom-field arms — not after.
     *
     * `evaluateRequiredFields` classifies it as `kind: "custom"` with a null
     * `dataType`, because it applies the same registry test and finds no
     * `custom_field_def` either. The verdict is right (false, matching
     * `transition_work_order`'s `LIKE 'builtin:%' THEN false`), but the SENTENCE
     * was not: the first version of this function fell through to "Not a
     * published field", which sends somebody to look for a custom field that was
     * never the subject. Caught by rendering `builtin:not_a_real_key` in the real
     * screen and reading what it said.
     */
    if (key.startsWith("builtin:")) {
      return {
        ...AUTOMATIC,
        kind: "misconfigured",
        note:
          "This stage requires a built-in field TruckBuddy does not recognise — " +
          "most likely a typo. Nothing can satisfy it; fix the stage in Configuration.",
      };
    }
    // A custom key with no data type is not a missing value at all — it is a
    // stage naming a field that was superseded or never published, and
    // `work_order_stage.required_fields` has no FK to `custom_field_def.key` to
    // stop it. README section 9a(d). Nothing on this work order can satisfy it,
    // so the remedy is the Configuration screen, not a control here.
    if (dataType === null) {
      return {
        ...AUTOMATIC,
        kind: "misconfigured",
        note:
          "Not a published field. Nothing on this work order can satisfy it — " +
          "fix the stage in Configuration.",
      };
    }
    /*
     * ── The third `unreachable` arm, and it now has a writer — TRBUD-249 ──
     *
     * This returned `unreachable` with "there is no route that changes them
     * afterwards", which was true and was the reason TRBUD-187's guard refused to
     * let a stage require a custom field at all: definable in Configuration,
     * offered in the stage editor, and fillable by nobody.
     *
     * `PATCH /work-orders/:id/custom-fields` is the writer. The card it points at
     * renders one control per field of the work order's OWN published schema —
     * the frozen `custom_field_schema` row `create_work_order` stamped on it, not
     * the type's current one, because `tb_validate_custom_fields` validates
     * against the stamped version and would raise *unknown custom field* for
     * anything published since.
     *
     * Gated on `editDueDate`, the same deliberate reuse `PATCH
     * /work-orders/:id/warranty` records: it is the capability `tb_app`'s
     * column-level UPDATE grants on this table are paired with, it carries the
     * `assigned` row scope for a mechanic, and STAFF hold it. Minting an
     * `editCustomFields` capability would ship a control nobody has been granted.
     */
    return {
      kind: "panel",
      anchor: "custom-fields",
      href: null,
      capability: "editDueDate",
      action: "Fill in the custom fields",
      note: null,
      deniedNote: "A mechanic, coordinator, manager or administrator fills them in.",
    };
  }

  switch (key) {
    case "builtin:location_at_open":
      return {
        ...AUTOMATIC,
        // TRBUD-247. `manualRemedy: false` is the whole point of this arm: the
        // capture happens inside `create_work_order` and there is no second
        // chance. The note now says so in the panel's own words, because a
        // reader who sees "no position was available at the time" and nothing
        // else will reasonably go looking for the control that fixes it.
        manualRemedy: false,
        note:
          "Captured from the asset's position when the work order is opened. " +
          "Nobody types it, and it is taken once: if it is blank no position was " +
          "available at the time and it cannot be filled in afterwards.",
      };

    /*
     * ── The two that WERE unreachable, and now have writers ──────────
     *
     * Both used to return `unreachable` with a note explaining that the value is
     * chosen at creation and no screen changes it. That was an accurate
     * description of a softlock: `tb_app` held the column grant, nothing used it,
     * and a stage requiring either refused every forward move for ever.
     *
     * Eden ruled on 8 September 2026 (TRBUD-187 remedy (a)) that these two — the
     * RFP families — get real writers. `PATCH /work-orders/:id/vendor` and
     * `PATCH /work-orders/:id/meter-reading` are those, and the panel now points
     * at the header card rather than apologising.
     *
     * Custom-field VALUES stay deferred by the same ruling, and remain
     * `unreachable` below. TRBUD-187's guard is what keeps a stage from requiring
     * one in the meantime.
     */
    case "builtin:vendor":
      return {
        kind: "header",
        anchor: "wo-header",
        href: null,
        capability: "advanceStages",
        action: "Set the vendor",
        note: null,
        deniedNote: "A mechanic, coordinator, manager or administrator sets it.",
      };
    case "builtin:meter_reading":
      return {
        kind: "header",
        anchor: "wo-header",
        href: null,
        capability: "advanceStages",
        action: "Set the meter reading",
        // The note stays, because the CONTROL is narrower than the requirement
        // sounds: it picks an existing reading for this asset rather than typing
        // a figure. A reading nobody observed is not a reading.
        note:
          "Pick a reading already recorded against this unit. The odometer and " +
          "engine hours come from Samsara, which has the final say on both.",
        deniedNote: "A mechanic, coordinator, manager or administrator sets it.",
      };

    case "builtin:assignee":
      return {
        kind: "header",
        anchor: "wo-header",
        href: null,
        capability: "advanceStages",
        action: "Assign somebody",
        note: null,
        deniedNote: "A coordinator, manager or administrator assigns work.",
      };

    case "builtin:due_date":
      return {
        kind: "header",
        anchor: "wo-header",
        href: null,
        capability: "editDueDate",
        action: "Set the due date",
        note: null,
        deniedNote: "A coordinator, manager or administrator sets due dates.",
      };

    case "builtin:root_cause":
      return {
        kind: "panel",
        anchor: "wo-procedure",
        href: null,
        capability: "advanceStages",
        action: "Record the root cause",
        note: null,
        deniedNote: "A mechanic, coordinator, manager or administrator records it.",
      };

    // The other half of Eden's "I cannot find where to enter them". The picker is
    // the last control inside the Procedure panel, at the foot of the screen, and
    // that panel is collapsed by default.
    case "builtin:fault_type":
      return {
        kind: "panel",
        anchor: "wo-procedure",
        href: null,
        capability: "advanceStages",
        action: "Record the fault type",
        note: null,
        deniedNote: "A mechanic, coordinator, manager or administrator records it.",
      };

    case "builtin:photo":
      return {
        kind: "panel",
        anchor: "wo-documents",
        href: null,
        capability: "uploadDocuments",
        action: "Attach a photo",
        note: null,
        deniedNote: "Anybody on staff, or the driver who raised it, can attach one.",
      };

    case "builtin:time_logged":
      return {
        kind: "panel",
        anchor: "wo-time",
        href: null,
        // `postTime`, matching `POST /work-orders/:id/time`. See `capability`
        // above for the mismatch this corrects.
        capability: "postTime",
        action: "Log time",
        note: null,
        deniedNote: "A mechanic, coordinator, manager or administrator logs time.",
      };

    case "builtin:parts_issued":
      return {
        kind: "panel",
        anchor: "wo-costs",
        href: null,
        capability: "postCosts",
        action: "Add a part cost",
        note: null,
        deniedNote: "A coordinator, manager or administrator posts costs.",
      };

    /*
     * ── ONE requirement, three ways to meet it — TRBUD-300 ─────────────
     *
     * An issued part (the Costs card's Issue part), a part cost (the Costs
     * card's Add cost — Eden, 25 September 2026, "sure"), a verified receipt
     * (the Documents card) or "No parts used" (the Costs card again). The entry
     * points at the COSTS card, because three of the four controls are there,
     * and the `instruction` names all four so the panel says it in one sentence
     * rather than listing four blockers where only one is needed.
     *
     * `postTime`, deliberately: it is what `POST /work-orders/:id/no-parts-used`
     * requires (Eden's third ruling — "people who can log time"), and it is the
     * widest of the writers' capabilities, so the mechanic who did the work gets
     * a button. Issuing a part needs `issuePart`, Add cost needs `postCosts` and
     * verifying a receipt needs `verifyDocuments`; each control on its own card
     * checks its own capability, as every control on those cards already does.
     */
    case "builtin:parts_accounted_for":
      return {
        kind: "panel",
        anchor: "wo-costs",
        href: null,
        capability: "postTime",
        action: "Record the parts",
        note: null,
        deniedNote: "A mechanic, coordinator, manager or administrator records it.",
        instruction:
          "Issue a part from inventory, add a part cost, attach the receipt, or mark No parts used.",
      };

    // ── The paperwork pair, and why they are two states not one ──────
    // Both need a file first. `/review/new` reads `workOrderId` and `documentId`
    // off the query string and renders an empty-state refusal without them, so
    // routing there before anything is attached would be a control that lands
    // nowhere. With a file attached the route is the one Eden asked for by name.
    case "builtin:final_invoice":
      if (ctx.loggableDocument === null) {
        return {
          kind: "prerequisite",
          anchor: "wo-documents",
          href: null,
          capability: "uploadDocuments",
          action: "Attach the vendor's invoice",
          note:
            "Attach the invoice and classify it as an invoice or a receipt. " +
            "Itemising it is the next step and it happens in the review queue.",
          deniedNote: "Anybody on staff can attach it; a coordinator itemises it.",
        };
      }
      return {
        kind: "route",
        anchor: null,
        href: reviewHref(ctx),
        capability: "confirmInvoice",
        action: "Itemise the invoice",
        note: null,
        deniedNote: "A coordinator, manager or administrator logs vendor invoices.",
      };

    case "builtin:receipt":
      if (ctx.loggableDocument === null) {
        return {
          kind: "prerequisite",
          anchor: "wo-documents",
          href: null,
          capability: "uploadDocuments",
          action: "Attach the receipt",
          note:
            "Attach the receipt or invoice and classify it as one. Somebody then " +
            "confirms it is the right paperwork for this job.",
          deniedNote: "Anybody on staff, or the driver who raised it, can attach one.",
        };
      }
      // Deliberately the Documents card rather than the review route: the
      // attestation is `verify_work_order_document`, whose button lives there,
      // and the review route offers it only as the end of the itemising flow.
      // A viewer holding `confirmInvoice` sees BOTH controls on that card, which
      // is the existing behaviour and not this panel's business to duplicate.
      return {
        kind: "panel",
        anchor: "wo-documents",
        href: null,
        capability: "verifyDocuments",
        action: "Verify the paperwork",
        note:
          "Somebody has to say this is the right receipt for this job. A malware " +
          "scan cannot answer that and neither can the person who uploaded it.",
        deniedNote: "A coordinator, manager or administrator verifies paperwork.",
      };

    default:
      /*
       * UNREACHABLE FOR A TYPO — that case is handled above, before the registry
       * test. This arm fires only when `builtinRequiredFields` gains a key and
       * nobody adds a route for it here, which is a different and worse fault:
       * the key is real, the gate is real, and the panel has nowhere to send
       * anybody. `requirement-entry.test.ts` asserts no REGISTERED key lands
       * here, so adding one to the vocabulary without routing it fails the gate
       * rather than shipping a shrug.
       */
      return {
        ...AUTOMATIC,
        kind: "misconfigured",
        note:
          "TruckBuddy has no entry point recorded for this requirement. It is a " +
          "real gate and this is a gap in the app — report it.",
      };
  }
}

/**
 * The context `requirementHasWriter` probes the table with — TRBUD-187.
 *
 * The writer question is asked from **Configuration**, where there is no work
 * order to name: an administrator is deciding whether a stage may gate on a key
 * at all, for every work order that will ever stand in it. So the probe carries a
 * nil UUID and no attached document.
 *
 * That is safe rather than merely convenient, and the safety is asserted rather
 * than argued. `ctx` is read by exactly two arms — `builtin:final_invoice` and
 * `builtin:receipt` — and both return `prerequisite` (no document) or `route`
 * (document), never `unreachable`. `requirement-entry.test.ts` walks the whole
 * registry under both contexts and asserts the writer verdict is identical, so a
 * future arm that made the answer context-dependent fails the gate here instead
 * of silently letting Configuration accept a key it should refuse.
 */
const WRITER_PROBE: RequirementEntryContext = {
  workOrderId: "00000000-0000-0000-0000-000000000000",
  loggableDocument: null,
};

/**
 * Can anything in TruckBuddy write this requirement on a work order that already
 * exists? — TRBUD-187.
 *
 * ## Why this is a function and not a list
 *
 * The three no-writer keys are already named, once, by `requirementEntryFor`'s
 * `unreachable` arms — that is what the requirements panel renders as "no route
 * writes this" in words. A second list in the Configuration validator would be
 * two statements of one fact, and the failure mode of two lists is that the day
 * somebody builds `PATCH /work-orders/:id/vendor` they update the panel and not
 * the guard, and Configuration goes on refusing a key that has become perfectly
 * writable. So this derives the answer from the table rather than restating it.
 *
 * ## The question is about EXISTING work orders, deliberately
 *
 * `create_work_order` does take `p_primary_vendor_id` and a `custom_fields` blob,
 * so a work order can be born carrying either. That does not make the requirement
 * satisfiable: a stage's `required_fields` gates every work order standing in the
 * stage, including the ones created before the field was published, and for those
 * there is no control anywhere. (As it happens `NewWorkOrderDialog` sends
 * `customFields: {}` unconditionally, so today no custom field value is ever
 * written at all — but the guard does not depend on that, because a create-time
 * control would not rescue the existing rows.)
 *
 * ## `misconfigured` answers false too
 *
 * A key that names no published field cannot be satisfied either, so it has no
 * writer in any useful sense. Callers are expected to have refused those cases
 * first with their own, more specific message — `validateRequiredFieldKeys` does —
 * and this ordering means a mistake there can only ever produce a vaguer refusal,
 * never a wrongly permitted key.
 */
export function requirementHasWriter(key: string, dataType: string | null): boolean {
  return requirementWriterStatus(key, dataType) === "writable";
}

/**
 * The three answers to "may a stage gate on this key" — TRBUD-247.
 *
 * `requirementHasWriter` is a boolean and stays one, because every existing
 * caller only wants to know whether to refuse. What the boolean could not carry
 * is WHY, and the two refusals have opposite remedies:
 *
 * - `no_writer` — nothing in TruckBuddy writes the value on a work order that
 *   already exists. The remedy is building the writer, and until somebody does,
 *   requiring the key strands every work order in the stage.
 * - `no_remedy` — something DID write it, exactly once, at creation, and it can
 *   never be written again. Every work order the writer missed is stranded
 *   permanently, and no amount of building fixes the ones already raised. The
 *   remedy is to gate on a different field.
 * - `writable` — somebody or something can still supply it.
 *
 * Keeping these apart is not decoration. A `no_remedy` key answering "has a
 * writer" is precisely how `builtin:location_at_open` came to be requireable: the
 * capture in `create_work_order` is a real writer, so the honest boolean was
 * `true`, and the guard let a stage gate on a column that is blank for ever on
 * any unit that had no GPS fix that day.
 */
export type RequirementWriterStatus = "writable" | "no_writer" | "no_remedy";

export function requirementWriterStatus(
  key: string,
  dataType: string | null,
): RequirementWriterStatus {
  const entry = requirementEntryFor(key, dataType, WRITER_PROBE);
  if (entry.kind === "unreachable" || entry.kind === "misconfigured") return "no_writer";
  // Read off the entry rather than off a list of keys, for the reason the header
  // of `requirementHasWriter` gives about lists: the day somebody builds a
  // re-capture route they will edit the arm, and a second list would go on
  // refusing a key that had become perfectly satisfiable.
  if (entry.kind === "automatic" && entry.manualRemedy === false) return "no_remedy";
  return "writable";
}

function reviewHref(ctx: RequirementEntryContext): string {
  const doc = ctx.loggableDocument;
  if (doc === null) return "/review";
  // Exactly the three parameters `DocumentsCard`'s "Verify invoice" button
  // sends, in the same order, so both doors land on the same form. `LogInvoice`
  // reads `workOrderId` and `documentId`; `attachmentId` is what its confirm step
  // verifies the attachment through.
  return (
    `/review/new?workOrderId=${encodeURIComponent(ctx.workOrderId)}` +
    `&documentId=${encodeURIComponent(doc.documentId)}` +
    `&attachmentId=${encodeURIComponent(doc.attachmentId)}`
  );
}
